import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Edit, MessageSquare, ChevronDown, Plus, Trash2, X, Search, CheckCircle, AlertTriangle, History, Shield, Clock, Download, FileSpreadsheet, Filter, RotateCcw } from 'lucide-react';

const USER_STATUSES = [
  { value: 'active', label: 'Active', color: 'bg-muted text-foreground' },
  { value: 'inactive', label: 'Inactive', color: 'bg-muted text-foreground' },
  { value: 'declined', label: 'Declined', color: 'bg-muted text-red-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-muted text-orange-800' }
];

const USER_TYPES = [
  { value: 'carrier', label: 'Carrier' },
  { value: 'broker', label: 'Broker' },
  { value: 'shipper', label: 'Shipper' },
  { value: 'driver', label: 'Driver' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'owner_operator', label: 'Owner Operator' },
  { value: 'other', label: 'Other' }
];

// System roles that control access to the TMS Web App and Driver Mobile App
const USER_ROLES = [
  { value: 'platform_admin', label: 'Platform Admin', description: 'Full system access', app: 'TMS Web App' },
  { value: 'company_admin', label: 'Company Admin', description: 'Company-level admin access', app: 'TMS Web App' },
  { value: 'manager', label: 'Manager', description: 'Team management access', app: 'TMS Web App' },
  { value: 'dispatcher', label: 'Dispatcher', description: 'Load dispatch & tracking', app: 'TMS Web App' },
  { value: 'accountant', label: 'Accountant', description: 'Financial & billing access', app: 'TMS Web App' },
  { value: 'hr_manager', label: 'HR Manager', description: 'Employee management', app: 'TMS Web App' },
  { value: 'sales_manager', label: 'Sales Manager', description: 'Sales & CRM access', app: 'TMS Web App' },
  { value: 'fleet_manager', label: 'Fleet Manager', description: 'Fleet & vehicle management', app: 'TMS Web App' },
  { value: 'driver', label: 'Driver', description: 'Driver mobile app access', app: 'Driver Mobile App' },
];

const PlatformUserManagement = ({ BACKEND_URL, fetchWithAuth }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Per-column filters state
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    email: '',
    role: '',
    userType: '',
    phone: '',
    subscription: '',
    mc: '',
    dot: '',
    company: ''
  });

  const hasActiveColumnFilters = useMemo(() => {
    return Object.values(columnFilters).some(Boolean);
  }, [columnFilters]);

  const resetColumnFilters = () => {
    setColumnFilters({
      name: '',
      email: '',
      role: '',
      userType: '',
      phone: '',
      subscription: '',
      mc: '',
      dot: '',
      company: ''
    });
  };

  // Filtered users memo combining global search, status filter & per-column filters
  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    return users.filter(user => {
      if (!user) return false;

      // 1. Column filters
      if (columnFilters.name && !String(user.full_name || '').toLowerCase().includes(columnFilters.name.toLowerCase().trim())) {
        return false;
      }
      if (columnFilters.email && !String(user.email || '').toLowerCase().includes(columnFilters.email.toLowerCase().trim())) {
        return false;
      }
      if (columnFilters.role && columnFilters.role !== 'all') {
        if (String(user.role || '').toLowerCase() !== columnFilters.role.toLowerCase()) return false;
      }
      if (columnFilters.userType && columnFilters.userType !== 'all') {
        if (String(user.user_type || '').toLowerCase() !== columnFilters.userType.toLowerCase()) return false;
      }
      if (columnFilters.phone && !String(user.phone || '').toLowerCase().includes(columnFilters.phone.toLowerCase().trim())) {
        return false;
      }
      if (columnFilters.subscription) {
        const subStr = (user.subscriptions || [])
          .map(s => `${s.bundle_name || s.plan_name || s.plan || ''} ${s.status || ''}`)
          .join(' ')
          .toLowerCase();
        if (!subStr.includes(columnFilters.subscription.toLowerCase().trim())) return false;
      }
      if (columnFilters.mc && !String(user.mc_number || '').toLowerCase().includes(columnFilters.mc.toLowerCase().trim())) {
        return false;
      }
      if (columnFilters.dot && !String(user.dot_number || '').toLowerCase().includes(columnFilters.dot.toLowerCase().trim())) {
        return false;
      }
      if (columnFilters.company) {
        const compStr = String(user.company_name || user.tenant_name || '').toLowerCase();
        if (!compStr.includes(columnFilters.company.toLowerCase().trim())) return false;
      }

      return true;
    });
  }, [users, columnFilters]);

  // Export data to CSV
  const handleDownloadCSV = () => {
    if (!filteredUsers || filteredUsers.length === 0) {
      toast.error('No user records available to export');
      return;
    }

    const headers = [
      'Name',
      'Email',
      'Role',
      'User Type',
      'Phone#',
      'Status',
      'Subscription',
      'MC#',
      'DOT#',
      'Company Name'
    ];

    const rows = filteredUsers.map(u => {
      const roleLabel = USER_ROLES.find(r => r.value === u.role)?.label || u.role || 'No Role';
      const typeLabel = USER_TYPES.find(t => t.value === u.user_type)?.label || u.user_type || 'Other';
      const subText = u.subscriptions?.length > 0
        ? u.subscriptions.filter(s => s.status === 'active').map(s => s.bundle_name || s.plan_name || s.plan || '').join('; ')
        : 'No subscription';

      return [
        `"${String(u.full_name || '').replace(/"/g, '""')}"`,
        `"${String(u.email || '').replace(/"/g, '""')}"`,
        `"${String(roleLabel).replace(/"/g, '""')}"`,
        `"${String(typeLabel).replace(/"/g, '""')}"`,
        `"${String(u.phone || '').replace(/"/g, '""')}"`,
        `"${String(u.status || 'active').replace(/"/g, '""')}"`,
        `"${String(subText).replace(/"/g, '""')}"`,
        `"${String(u.mc_number || '').replace(/"/g, '""')}"`,
        `"${String(u.dot_number || '').replace(/"/g, '""')}"`,
        `"${String(u.company_name || u.tenant_name || '').replace(/"/g, '""')}"`
      ].join(',');
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `platform_users_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredUsers.length} user records to CSV`);
  };

  // Export data to Excel (.xls format)
  const handleDownloadExcel = () => {
    if (!filteredUsers || filteredUsers.length === 0) {
      toast.error('No user records available to export');
      return;
    }

    const headers = ['Name', 'Email', 'Role', 'User Type', 'Phone#', 'Status', 'Subscription', 'MC#', 'DOT#', 'Company Name'];
    const rows = filteredUsers.map(u => {
      const roleLabel = USER_ROLES.find(r => r.value === u.role)?.label || u.role || 'No Role';
      const typeLabel = USER_TYPES.find(t => t.value === u.user_type)?.label || u.user_type || 'Other';
      const subText = u.subscriptions?.length > 0
        ? u.subscriptions.filter(s => s.status === 'active').map(s => s.bundle_name || s.plan_name || s.plan || '').join('; ')
        : 'No subscription';

      return [
        u.full_name || '',
        u.email || '',
        roleLabel,
        typeLabel,
        u.phone || '',
        u.status || 'active',
        subText,
        u.mc_number || '',
        u.dot_number || '',
        u.company_name || u.tenant_name || ''
      ];
    });

    const tsvContent = '\uFEFF' + [headers.join('\t'), ...rows.map(r => r.map(c => String(c).replace(/\t/g, ' ')).join('\t'))].join('\r\n');
    const blob = new Blob([tsvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `platform_users_${timestamp}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredUsers.length} user records to Excel (.xls)`);
  };
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    password: '',
    phone: '',
    company_id: '',
    role: 'company_admin',
    user_type: 'other',
    status: 'active',
    bundle_id: ''
  });

  // Bundles for assignment
  const [bundles, setBundles] = useState([]);
  
  // Edit user form
  const [editUser, setEditUser] = useState({
    full_name: '',
    phone: '',
    user_type: 'other',
    status: 'active',
    role: 'company_admin',
    password: ''
  });

  // Tenants data
  const [tenants, setTenants] = useState([]);

  // Comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  // Audit Log
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);

  // FMCSA Carrier Lookup for Create User
  const [carrierSearchQuery, setCarrierSearchQuery] = useState('');
  const [carrierSearchLoading, setCarrierSearchLoading] = useState(false);
  const [carrierInfo, setCarrierInfo] = useState(null);

  const lookupCarrier = async () => {
    if (!carrierSearchQuery.trim()) {
      toast.error('Please enter a DOT#, MC#, or company name');
      return;
    }

    setCarrierSearchLoading(true);
    setCarrierInfo(null);

    try {
      const response = await fetchWithAuth(
        `${BACKEND_URL}/api/fmcsa/carrier/lookup?query=${encodeURIComponent(carrierSearchQuery.trim())}`
      );

      if (response.ok) {
        const data = await response.json();
        
        // Handle single carrier result
        if (data.carrier) {
          setCarrierInfo(data.carrier);
          // Auto-fill form fields
          setNewUser(prev => ({
            ...prev,
            full_name: data.carrier.legal_name || prev.full_name,
            phone: data.carrier.phone || prev.phone
          }));
          toast.success('Carrier found! Form fields auto-filled (Tenant must be selected manually).');
        } 
        // Handle multiple results - take the first one
        else if (data.carriers && data.carriers.length > 0) {
          const carrier = data.carriers[0];
          setCarrierInfo(carrier);
          setNewUser(prev => ({
            ...prev,
            full_name: carrier.legal_name || prev.full_name,
            phone: carrier.phone || prev.phone
          }));
          toast.success(`Found ${data.carriers.length} carrier(s). First result auto-filled (Tenant must be selected manually).`);
        } else {
          toast.info('No carrier found matching your search');
        }
      } else {
        const error = await response.json();
        if (response.status === 404) {
          toast.info('No carrier found matching your search');
        } else {
          toast.error(error.detail || 'Carrier lookup failed');
        }
      }
    } catch (error) {
      console.error('Carrier lookup error:', error);
      toast.error('Failed to lookup carrier');
    } finally {
      setCarrierSearchLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/tenants`);
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const fetchBundles = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/bundles`);
      if (response.ok) {
        const data = await response.json();
        setBundles(data.bundles || []);
      }
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
    }
  }, [fetchWithAuth, BACKEND_URL]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchQuery, fetchWithAuth, BACKEND_URL]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/stats/overview`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [fetchWithAuth, BACKEND_URL]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchTenants();
    fetchBundles();
  }, [fetchUsers, fetchStats, fetchBundles]);

  const handleCreateUser = async () => {
    try {
      if (!newUser.email || !newUser.full_name || !newUser.password || !newUser.company_id) {
        toast.error('Please fill in all required fields (Name, Email, Password, Tenant)');
        return;
      }

      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        const createdUser = await response.json();
        
        // If bundle is selected, assign it to the user
        if (newUser.bundle_id && newUser.bundle_id !== 'none') {
          try {
            await fetchWithAuth(`${BACKEND_URL}/api/bundles/assign`, {
              method: 'POST',
              body: JSON.stringify({
                bundle_id: newUser.bundle_id,
                entity_type: 'user',
                entity_id: createdUser.id || createdUser.user_id,
                notes: 'Assigned during user creation'
              })
            });
          } catch (bundleError) {
            console.error('Failed to assign bundle:', bundleError);
            toast.warning('User created but bundle assignment failed');
          }
        }
        
        toast.success('User created successfully');
        setShowCreateModal(false);
        setNewUser({
          email: '',
          full_name: '',
          password: '',
          phone: '',
          company_id: '',
          role: 'company_admin',
          user_type: 'other',
          status: 'active',
          bundle_id: ''
        });
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
      console.error(error);
    }
  };

  const handleEditUser = async () => {
    try {
      const updateData = {};
      if (editUser.full_name) updateData.full_name = editUser.full_name;
      if (editUser.phone) updateData.phone = editUser.phone;
      if (editUser.user_type) updateData.user_type = editUser.user_type;
      if (editUser.status) updateData.status = editUser.status;
      if (editUser.role) updateData.role = editUser.role;
      if (editUser.password) updateData.password = editUser.password;

      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('User updated successfully');
        setShowEditModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
      console.error(error);
    }
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`User status updated to ${newStatus}`);
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('User deactivated successfully');
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to deactivate user');
      }
    } catch (error) {
      toast.error('Failed to deactivate user');
      console.error(error);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditUser({
      full_name: user.full_name || '',
      phone: user.phone || '',
      user_type: user.user_type || 'other',
      status: user.status || 'active',
      role: user.role || 'company_admin',
      password: ''
    });
    setShowEditModal(true);
  };

  const openCommentsModal = async (user) => {
    setSelectedUser(user);
    setShowCommentsModal(true);
    setLoadingComments(true);
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${user.id}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const openAuditModal = async (user) => {
    setSelectedUser(user);
    setShowAuditModal(true);
    setLoadingAuditLogs(true);
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${user.id}/audit-log`);
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      } else {
        // Show sample data structure for demo
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${selectedUser.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content: newComment })
      });

      if (response.ok) {
        const data = await response.json();
        setComments([...comments, data.comment]);
        setNewComment('');
        toast.success('Comment added');
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      toast.error('Failed to add comment');
      console.error(error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/admin/users/${selectedUser.id}/comments/${commentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setComments(comments.filter(c => c.id !== commentId));
        toast.success('Comment deleted');
      } else {
        toast.error('Failed to delete comment');
      }
    } catch (error) {
      toast.error('Failed to delete comment');
      console.error(error);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = USER_STATUSES.find(s => s.value === status) || USER_STATUSES[0];
    return <Badge className={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-foreground">{stats.total_users}</div>
              <div className="text-sm text-muted-foreground">Total Users</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-foreground">{stats.active_users}</div>
              <div className="text-sm text-muted-foreground">Active Users</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-foreground">{stats.inactive_users}</div>
              <div className="text-sm text-muted-foreground">Inactive Users</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-foreground">{stats.total_companies_with_users}</div>
              <div className="text-sm text-muted-foreground">Companies</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader className="border-b border-border">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CardTitle className="text-xl font-bold">User Management</CardTitle>
              <Badge variant="outline" className="text-xs font-mono bg-muted text-foreground">
                {filteredUsers.length} {filteredUsers.length === 1 ? 'User' : 'Users'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {/* Export Data Dropdown Button next to Create User */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-9 text-xs border-border bg-background hover:bg-muted font-semibold flex items-center gap-1.5"
                    title="Export user data to CSV or Excel"
                    data-testid="export-user-data-btn"
                  >
                    <Download className="w-4 h-4 text-emerald-500" />
                    <span>Export Data</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleDownloadCSV} className="cursor-pointer text-xs flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                    <span>Download CSV (.csv)</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadExcel} className="cursor-pointer text-xs flex items-center gap-2">
                    <Download className="w-4 h-4 text-blue-600" />
                    <span>Download Excel (.xls)</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button onClick={() => setShowCreateModal(true)} className="bg-primary hover:bg-primary/90 h-9 text-xs" data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="md:col-span-2">
              <Input
                placeholder="Search by name, email, company, MC#, or DOT#..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="user-search-input"
              />
            </div>
            <div>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {USER_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Users Table - Spreadsheet Style */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-muted-foreground text-4xl mb-3">🔍</div>
              <p className="text-foreground font-semibold">No matching users found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting or clearing your column filters.</p>
              {hasActiveColumnFilters && (
                <Button size="sm" variant="outline" onClick={resetColumnFilters} className="mt-3 text-xs border-amber-500/40 text-amber-600">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Clear Column Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-300 rounded-lg">
              <table className="w-full border-collapse min-w-[1400px]">
                <thead>
                  {/* Column Header Titles */}
                  <tr className="bg-muted">
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>Name</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>Email</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>Role</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>User Type</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>Phone#</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>Subscription</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>MC#</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>DOT#</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground uppercase tracking-wider border-b border-r border-gray-300">
                      <div className="flex items-center justify-between">
                        <span>Company Name</span>
                        <Filter className="w-3 h-3 text-muted-foreground opacity-50" />
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-center text-xs font-bold text-foreground uppercase tracking-wider border-b border-gray-300">Actions</th>
                  </tr>

                  {/* Column Filters Input Controls Row */}
                  <tr className="bg-muted/60 border-b-2 border-gray-300">
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter Name..."
                        value={columnFilters.name}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, name: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter Email..."
                        value={columnFilters.email}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, email: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Select
                        value={columnFilters.role || "all"}
                        onValueChange={(val) => setColumnFilters(prev => ({ ...prev, role: val === "all" ? "" : val }))}
                      >
                        <SelectTrigger className="h-7 text-[11px] bg-background border-border">
                          <SelectValue placeholder="All Roles" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Roles</SelectItem>
                          {USER_ROLES.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Select
                        value={columnFilters.userType || "all"}
                        onValueChange={(val) => setColumnFilters(prev => ({ ...prev, userType: val === "all" ? "" : val }))}
                      >
                        <SelectTrigger className="h-7 text-[11px] bg-background border-border">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {USER_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter Phone..."
                        value={columnFilters.phone}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, phone: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter Sub..."
                        value={columnFilters.subscription}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, subscription: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter MC#..."
                        value={columnFilters.mc}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, mc: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter DOT#..."
                        value={columnFilters.dot}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, dot: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 border-r border-gray-300">
                      <Input
                        placeholder="Filter Company..."
                        value={columnFilters.company}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, company: e.target.value }))}
                        className="h-7 text-xs bg-background border-border"
                      />
                    </td>
                    <td className="p-1.5 text-center">
                      {hasActiveColumnFilters ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={resetColumnFilters}
                          className="h-7 px-2 text-[11px] border-amber-500/40 text-amber-600 hover:bg-amber-500/10 font-medium flex items-center gap-1 mx-auto"
                          title="Clear all column filters"
                        >
                          <RotateCcw className="w-3 h-3" />
                          <span>Clear</span>
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground uppercase font-semibold">Filter</span>
                      )}
                    </td>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-gray-200">
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id} className={index % 2 === 0 ? 'bg-card' : 'bg-muted'}>
                      <td className="px-3 py-3 text-sm text-foreground border-r border-border whitespace-nowrap">{user.full_name}</td>
                      <td className="px-3 py-3 text-sm text-foreground border-r border-border whitespace-nowrap">{user.email}</td>
                      <td className="px-3 py-3 text-sm border-r border-border whitespace-nowrap">
                        <Badge variant="outline" className={`capitalize ${
                          user.role === 'platform_admin' ? 'border-red-500 text-red-600' :
                          user.role === 'company_admin' ? 'border-purple-500 text-purple-600' :
                          user.role === 'driver' ? 'border-green-500 text-green-600' :
                          'border-blue-500 text-blue-600'
                        }`}>
                          {USER_ROLES.find(r => r.value === user.role)?.label || user.role || 'No Role'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-sm border-r border-border whitespace-nowrap">
                        <Badge variant="outline" className="capitalize">
                          {USER_TYPES.find(t => t.value === user.user_type)?.label || user.user_type || 'Other'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-sm text-foreground border-r border-border whitespace-nowrap">{user.phone || '-'}</td>
                      <td className="px-3 py-3 text-sm border-r border-border whitespace-nowrap">
                        {user.subscriptions?.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {user.subscriptions.filter(s => s.status === 'active').map((sub, idx) => (
                              <Badge key={idx} className="bg-muted text-purple-800 text-xs">
                                {sub.bundle_name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">No subscription</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-foreground border-r border-border whitespace-nowrap">{user.mc_number || '-'}</td>
                      <td className="px-3 py-3 text-sm text-foreground border-r border-border whitespace-nowrap">{user.dot_number || '-'}</td>
                      <td className="px-3 py-3 text-sm text-foreground border-r border-border whitespace-nowrap">{user.company_name || '-'}</td>
                      <td className="px-3 py-3 text-sm border-border">
                        <div className="flex items-center justify-center gap-2">
                          {/* Edit Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditModal(user)}
                            className="h-8 px-2"
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>

                          {/* Status Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" className="h-8 px-2 min-w-[100px]">
                                {getStatusBadge(user.status || 'active')}
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {USER_STATUSES.map(status => (
                                <DropdownMenuItem 
                                  key={status.value}
                                  onClick={() => handleUpdateStatus(user.id, status.value)}
                                  className={user.status === status.value ? 'bg-muted' : ''}
                                >
                                  <Badge className={`${status.color} mr-2`}>{status.label}</Badge>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* Comments Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCommentsModal(user)}
                            className="h-8 px-2"
                            title="View Comments"
                          >
                            <MessageSquare className="w-4 h-4" />
                            {user.comments?.length > 0 && (
                              <span className="ml-1 bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
                                {user.comments.length}
                              </span>
                            )}
                          </Button>

                          {/* Audit Log Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAuditModal(user)}
                            className="h-8 px-2"
                            title="View Activity Log"
                            data-testid={`audit-btn-${user.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setCarrierSearchQuery('');
          setCarrierInfo(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          
          {/* FMCSA Carrier Lookup Section */}
          <div className="border rounded-lg p-4 bg-muted mb-4">
            <Label className="text-sm font-medium text-foreground mb-2 block">
              <Search className="w-4 h-4 inline mr-1" />
              FMCSA Carrier Lookup (Auto-fill from DOT#, MC#, or company name)
            </Label>
            <div className="flex gap-2">
              <Input
                value={carrierSearchQuery}
                onChange={(e) => setCarrierSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && lookupCarrier()}
                placeholder="Enter DOT#, MC#, or company name..."
                className="flex-1 bg-card"
              />
              <Button 
                type="button" 
                onClick={lookupCarrier}
                disabled={carrierSearchLoading}
                className="bg-primary hover:bg-primary/90"
              >
                {carrierSearchLoading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
            {carrierInfo && (
              <div className="mt-3 p-3 bg-card rounded border text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-foreground">{carrierInfo.legal_name}</span>
                  {carrierInfo.allow_to_operate === 'Y' && !carrierInfo.out_of_service ? (
                    <Badge className="bg-muted text-foreground text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />Authorized
                    </Badge>
                  ) : (
                    <Badge className="bg-muted text-red-800 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />Not Authorized
                    </Badge>
                  )}
                </div>
                <div className="text-muted-foreground text-xs">
                  {carrierInfo.dot_number && <span className="mr-3">DOT# {carrierInfo.dot_number}</span>}
                  {carrierInfo.mc_number && <span className="mr-3">MC# {carrierInfo.mc_number}</span>}
                </div>
                {carrierInfo.physical_address && (
                  <div className="text-muted-foreground text-xs mt-1">{carrierInfo.physical_address}</div>
                )}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input
                value={newUser.full_name}
                onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                placeholder="Full Name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="email@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Phone#</Label>
              <Input
                value={newUser.phone}
                onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Tenant (Company) *</Label>
              <Select 
                value={newUser.company_id} 
                onValueChange={(value) => setNewUser({...newUser, company_id: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">User Type</Label>
              <Select 
                value={newUser.user_type} 
                onValueChange={(value) => setNewUser({...newUser, user_type: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-sm font-medium">System Role *</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(value) => setNewUser({...newUser, role: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center justify-between w-full">
                        <span>{role.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">({role.app})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {USER_ROLES.find(r => r.value === newUser.role)?.description || 'Select a role to see description'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Password *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Enter password"
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setNewUser({...newUser, password: generatePassword()})}
                >
                  Generate
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <Select 
                value={newUser.status} 
                onValueChange={(value) => setNewUser({...newUser, status: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-sm font-medium">Subscription Bundle</Label>
              <Select 
                value={newUser.bundle_id || undefined} 
                onValueChange={(value) => setNewUser({...newUser, bundle_id: value})}
              >
                <SelectTrigger className="mt-1" data-testid="create-user-bundle-select">
                  <SelectValue placeholder="Select a bundle (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Bundle</SelectItem>
                  {bundles.filter(b => b.is_active).map(bundle => (
                    <SelectItem key={bundle.id} value={bundle.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{bundle.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          ${bundle.monthly_price}/mo
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newUser.bundle_id && newUser.bundle_id !== 'none' && bundles.find(b => b.id === newUser.bundle_id) && (
                <p className="text-xs text-muted-foreground mt-1">
                  Includes: {bundles.find(b => b.id === newUser.bundle_id)?.products?.map(p => p.product_name).join(', ')}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} data-testid="create-user-cancel-btn">Cancel</Button>
            <Button onClick={handleCreateUser} className="bg-primary hover:bg-primary/90" data-testid="create-user-submit-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User: {selectedUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-sm font-medium">Name</Label>
              <Input
                value={editUser.full_name}
                onChange={(e) => setEditUser({...editUser, full_name: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Phone#</Label>
              <Input
                value={editUser.phone}
                onChange={(e) => setEditUser({...editUser, phone: e.target.value})}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">User Type</Label>
              <Select 
                value={editUser.user_type} 
                onValueChange={(value) => setEditUser({...editUser, user_type: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <Select 
                value={editUser.status} 
                onValueChange={(value) => setEditUser({...editUser, status: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-sm font-medium">System Role</Label>
              <Select 
                value={editUser.role} 
                onValueChange={(value) => setEditUser({...editUser, role: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center justify-between w-full">
                        <span>{role.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">({role.app})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {USER_ROLES.find(r => r.value === editUser.role)?.description || 'Controls access level'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">New Password (leave empty to keep)</Label>
              <Input
                type="text"
                value={editUser.password}
                onChange={(e) => setEditUser({...editUser, password: e.target.value})}
                placeholder="Enter new password"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditUser} className="bg-primary hover:bg-primary/90">Update User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comments Modal */}
      <Dialog open={showCommentsModal} onOpenChange={setShowCommentsModal}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Comments for {selectedUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Add Comment */}
            <div className="flex gap-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment about this customer's history..."
                rows={2}
                className="flex-1"
              />
              <Button onClick={handleAddComment} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Comments List */}
            <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
              {loadingComments ? (
                <div className="p-4 text-center text-muted-foreground">Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">No comments yet</div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="p-3 hover:bg-muted">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm text-foreground">{comment.content}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {comment.created_by_name} • {new Date(comment.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommentsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Activity Audit Log Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Activity Log: {selectedUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              User activity history and audit trail
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {loadingAuditLogs ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No activity logs found for this user</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Activity will be recorded once the backend endpoint is implemented.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="flex gap-3 p-3 bg-muted rounded-lg">
                    <div className={`p-2 rounded-full h-fit ${
                      log.action === 'login' ? 'bg-green-100 text-green-600' :
                      log.action === 'role_change' ? 'bg-purple-100 text-purple-600' :
                      log.action === 'status_change' ? 'bg-orange-100 text-orange-600' :
                      log.action === 'profile_update' ? 'bg-blue-100 text-blue-600' :
                      log.action === 'feature_access' ? 'bg-cyan-100 text-cyan-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {log.action === 'login' ? <CheckCircle className="w-4 h-4" /> :
                       log.action === 'role_change' ? <Shield className="w-4 h-4" /> :
                       log.action === 'status_change' ? <AlertTriangle className="w-4 h-4" /> :
                       <Clock className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium capitalize">{log.action?.replace(/_/g, ' ')}</p>
                        <span className="text-xs text-muted-foreground">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {log.details || log.description || 'No details available'}
                      </p>
                      {log.ip_address && (
                        <p className="text-xs text-muted-foreground mt-1">
                          IP: {log.ip_address}
                        </p>
                      )}
                      {log.performed_by && log.performed_by !== selectedUser?.email && (
                        <p className="text-xs text-muted-foreground">
                          By: {log.performed_by}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformUserManagement;
